import React, { memo, useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Modal } from 'antd'
import {
  createSession,
  getCardID,
  getCardStages,
  getCardData,
  getDataLoading,
  getVisibleFormData,
  getCardStagesItems,
  getCardCurrentStage,
  getCardStagesHistory,
} from '../../../selectors'
import {
  CardActions,
  useCardActions,
  CardActionConfig,
} from '../../../../../../components/v2/card-actions'
import { isFormDataRequired } from '../../../../../../../lib/utils/collections'
import { updateFormData } from '../../../../../../store/claim_request/action-creators'
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch/useAppDispatch'
import { HStack } from '../../../../../../ui/Stack'
import { HSteps } from '../../../../../../ui/Steps'
import { UserRoles } from '../../../../../../constants/roles'
import { getStageCodeFromFormStages } from '../../../../../../lib/stage-conflict'

interface FormHeaderProps {
  showTitle?: boolean
}

export const FormHeader: React.FC<FormHeaderProps> = memo(() => {
  const dispatch = useAppDispatch()
  const id = useSelector(getCardID)
  const stageObj = useSelector(getCardStages)
  const cardData = useSelector(getCardData)
  const { sessionData } = useSelector(createSession)
  const dataIsLoading = useSelector(getDataLoading)
  const visibleFormData = useSelector(getVisibleFormData)
  const stagesItems = useSelector(getCardStagesItems)
  const currentStage = useSelector(getCardCurrentStage)
  const stagesHistory = useSelector(getCardStagesHistory)
  const [isStageTransitioning, setIsStageTransitioning] = useState(false)
  const stageTransitionLockRef = useRef(false)

  const handleToClaim = useCallback(async () => {
    if (!id) return
    if (stageTransitionLockRef.current) return
    const data = visibleFormData
    if (data) {
      const isRequiredFields = isFormDataRequired(data.resultObject, data.resultSchema)
      if (!isRequiredFields) {
        Modal.warning({
          title: 'Предупреждение',
          content: 'Заполните все обязательные поля',
        })
        return
      } else {
        stageTransitionLockRef.current = true
        setIsStageTransitioning(true)
        try {
          const from_stage = getStageCodeFromFormStages(stageObj)
          await dispatch(
            updateFormData(id, { stage: 'approved', ...(from_stage ? { from_stage } : {}) }),
          )
        } finally {
          stageTransitionLockRef.current = false
          setIsStageTransitioning(false)
        }
      }
    }
  }, [id, visibleFormData, stageObj, dispatch])

  const handleTransferToService = useCallback(() => {
    if (!id || id === 'new') return
    dispatch(updateFormData(id, { is_sent_to_service: true }))
  }, [id, dispatch])

  const isSalesManager = useMemo(() => {
    return sessionData?.roles.includes(UserRoles.SALES_MANAGER)
  }, [sessionData])

  const isSentToService = useMemo(() => {
    const formValues = visibleFormData?.resultObject as Record<string, unknown> | undefined
    return Boolean(cardData?.is_sent_to_service || formValues?.['is_sent_to_service'])
  }, [cardData, visibleFormData])

  const buttonsDisabled = useMemo(() => {
    if (dataIsLoading || !id) {
      return true
    }
    const hasAccess =
      sessionData?.roles.includes(UserRoles.ADMIN) ||
      sessionData?.roles.includes(UserRoles.CLAIMS_ACCESS)
    if (stageObj?.items !== undefined && stageObj?.current !== undefined && hasAccess) {
      return stageObj?.items?.[stageObj?.current]?.code !== 'new'
    }
    return !hasAccess
  }, [dataIsLoading, stageObj, sessionData, id])

  const actionsConfig = useMemo(
    (): CardActionConfig[] => [
      {
        key: 'to-service',
        label: isSentToService ? 'В службе сервиса' : 'Передать в службу сервиса',
        type: 'default',
        onClick: handleTransferToService,
        disabled: dataIsLoading || id === 'new' || isSentToService,
        condition: isSalesManager,
        order: 0,
      },
      {
        key: 'to-claim',
        label: 'Принять',
        type: 'primary',
        onClick: handleToClaim,
        isPrimary: true,
        disabled: buttonsDisabled || id === 'new' || isStageTransitioning,
        loading: isStageTransitioning,
        condition: true,
        order: 1,
      },
    ],
    [
      handleToClaim,
      buttonsDisabled,
      handleTransferToService,
      dataIsLoading,
      id,
      isSentToService,
      isSalesManager,
      isStageTransitioning,
    ],
  )

  const { primaryAction, secondaryActions } = useCardActions({ actions: actionsConfig })

  return (
    <HStack gap={'8'} className={'mapWrapper'} wrap>
      <div>
        {stagesItems && (
          <HSteps
            items={Object.values(stagesItems)}
            current={currentStage}
            history={stagesHistory}
          />
        )}
      </div>
      <CardActions primaryAction={primaryAction} secondaryActions={secondaryActions} />
    </HStack>
  )
})

FormHeader.displayName = 'FormHeader'
